/**
 * Tools actions
 */

import { NgRedux } from '@angular-redux/store';
import { Injectable } from '@angular/core';
import { IAppState } from '@app/store';
import { IAction, IDocumentOperationOptions } from '@shared/types';
import { generateId } from '@shared/utils';
import { get } from 'lodash';

export const SEND_HTTP_REQUEST = 'SEND_HTTP_REQUEST';
export const SEND_HTTP_REQUEST_SUCCESS = 'SEND_HTTP_REQUEST_SUCCESS';
export const SEND_HTTP_REQUEST_ERROR = 'SEND_HTTP_REQUEST_ERROR';
export const CLICK_NAVIGATE_BUTTON = 'CLICK_NAVIGATE_BUTTON';

export const FETCH_API_INFO = 'FETCH_API_INFO';
export const FETCH_API_INFO_SUCCESS = 'FETCH_API_INFO_SUCCESS';
export const FETCH_API_INFO_ERROR = 'FETCH_API_INFO_ERROR';

export const FETCH_COMPONENT_INFO = 'FETCH_COMPONENT_INFO';
export const FETCH_COMPONENT_INFO_SUCCESS = 'FETCH_COMPONENT_INFO_SUCCESS';
export const FETCH_COMPONENT_INFO_ERROR = 'FETCH_COMPONENT_INFO_ERROR';

export const APPLICATION_STARTUP = 'APPLICATION_STARTUP';
export const APPLICATION_STARTUP_SUCCESS = 'APPLICATION_STARTUP_SUCCESS';

export const UI_CLEAR_SCHEMA_CACHE = 'UI_CLEAR_SCHEMA_CACHE';

export const ADD_ACTION_STATUS = 'ADD_ACTION_STATUS';
export const ADD_ACTION_STATUS_ERROR = 'ADD_ACTION_STATUS_ERROR';
export const ADD_ACTION_STATUS_SUCCESS = 'ADD_ACTION_STATUS_SUCCESS';
export const REMOVE_ALL_ACTION_STATUSES = 'REMOVE_ALL_ACTION_STATUSES';

@Injectable()
export class ToolsActions {
  public constructor(public ngRedux: NgRedux<IAppState>) {}

  /**
   * Actions creator for sendHttpRequest
   *
   * @returns
   */
  public sendHttpRequestAction(request, options): IAction {
    return {
      type: SEND_HTTP_REQUEST,
      payload: {
        request,
        options,
      },
    };
  }

  /**
   * Actions creator for clickNavigateButton
   *
   * @returns
   */
  public clickNavigateButtonAction(options?): IAction {
    return {
      type: CLICK_NAVIGATE_BUTTON,
      payload: {
        options,
      },
    };
  }

  /**
   * Actions creator for sendHttpRequest success response
   *
   * @param response - response object
   * @returns
   */
  public sendHttpRequestSuccessAction(response, request, options): IAction {
    return {
      type: SEND_HTTP_REQUEST_SUCCESS,
      payload: { response, request, options },
    };
  }

  /**
   * Actions creator for sendHttpRequest error response
   *
   * @param errors - errors
   * @returns
   */
  public sendHttpRequestErrorAction(errors, request, options): IAction {
    return {
      type: SEND_HTTP_REQUEST_ERROR,
      payload: { errors, request, options },
    };
  }

  public sendHttpRequest(request, userOptions?): Promise<any> {
    const options: any = Object.assign(
      { requestId: generateId() },
      userOptions,
    );
    this.ngRedux.dispatch(this.sendHttpRequestAction(request, options));

    return this.resultAsPromised(options.requestId);
  }

  public clickNavigateButton(options?): void {
    this.ngRedux.dispatch(this.clickNavigateButtonAction(options));
  }

  public resultAsPromised(requestId): Promise<any> {
    const selectionPath: string[] = ['tools', 'operationResult'];

    return this.ngRedux
      .select(selectionPath)
      .find((result: any) => get(result, 'options.requestId') === requestId)
      .toPromise()
      .then((operationResult: any): any => {
        if (!operationResult.response) throw new Error(operationResult.errors);

        return operationResult.response;
      });
  }

  /*
   * Environment information
   */

  public fetchInfoAction(options: IDocumentOperationOptions): IAction {
    return {
      type: FETCH_API_INFO,
      payload: {
        options,
      },
    };
  }

  public fetchInfoSuccessAction(response, options): IAction {
    return {
      type: FETCH_API_INFO_SUCCESS,
      payload: {
        response,
        options,
      },
    };
  }

  public fetchInfoErrorAction(response, options): IAction {
    return {
      type: FETCH_API_INFO_ERROR,
      payload: {
        response,
        options,
      },
    };
  }

  /**
   * Fetches Venues Inventory API info
   */
  public fetchInfo(): Promise<any> {
    const reqOptions: IDocumentOperationOptions = Object.assign({
      requestId: generateId(),
    });
    this.ngRedux.dispatch(this.fetchInfoAction(reqOptions));

    return this.resultAsPromised(reqOptions.requestId);
  }

  public applicationStartupAction(): IAction {
    return {
      type: APPLICATION_STARTUP,
    };
  }

  public startUp(): void {
    this.ngRedux.dispatch(this.applicationStartupAction());
  }

  public applicationStartupSuccessAction(): IAction {
    return {
      type: APPLICATION_STARTUP_SUCCESS,
    };
  }

  public applicationStartupSuccess(): void {
    this.ngRedux.dispatch(this.applicationStartupSuccessAction());
  }

  /*
   * Simple Service Registry information
   */

  public fetchComponentInfoAction(): IAction {
    return {
      type: FETCH_COMPONENT_INFO,
    };
  }

  public fetchComponentInfoSuccessAction(components): IAction {
    return {
      type: FETCH_COMPONENT_INFO_SUCCESS,
      payload: {
        components,
      },
    };
  }

  public fetchComponentInfoErrorAction(errors): IAction {
    return {
      type: FETCH_COMPONENT_INFO_ERROR,
      payload: {
        errors,
      },
    };
  }

  public fetchComponentInfo(): void {
    this.ngRedux.dispatch(this.fetchComponentInfoAction());
  }

  public clearSchemaCache(): IAction {
    return this.ngRedux.dispatch({ type: UI_CLEAR_SCHEMA_CACHE });
  }

  public removeAllActionStatuses(): void {
    this.ngRedux.dispatch(this.removeAllActionStatusesAction());
  }

  public addActionStatusError({ requestId, trackedAction }): IAction {
    return {
      type: ADD_ACTION_STATUS_ERROR,
      payload: {
        requestId,
        status: 'error',
        actionPayload: trackedAction.payload,
        actionType: trackedAction.type,
      },
    };
  }

  public removeActionStatus({ requestId, trackedAction }): IAction {
    return {
      type: ADD_ACTION_STATUS_SUCCESS,
      payload: {
        requestId,
        actionPayload: trackedAction.payload,
        actionType: trackedAction.type,
      },
    };
  }

  public removeAllActionStatusesAction(): IAction {
    return {
      type: REMOVE_ALL_ACTION_STATUSES,
    };
  }

  public addActionStatus({ requestId, trackedAction }): IAction {
    return {
      type: ADD_ACTION_STATUS,
      payload: {
        requestId,
        status: 'in_progress',
        actionPayload: trackedAction.payload,
        actionType: trackedAction.type,
      },
    };
  }
}
